/**
 * ImageViewer.tsx
 * Zoomable, pannable image viewer with scrollbars
 * 
 * Features:
 * - Zoom in/out via props
 * - Drag to pan
 * - Native scrollbars (overflow: auto)
 * - Grab cursor for drag indication
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';

interface ImageViewerProps {
  src: string;
  alt?: string;
  zoom: number;
  className?: string;
}

export const ImageViewer: React.FC<ImageViewerProps> = ({
  src,
  alt = 'Captured image',
  zoom,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 });

  // Debug: log when src changes
  useEffect(() => {
    console.log('[ImageViewer] Received src:', {
      length: src?.length || 0,
      preview: src?.substring(0, 100),
      isValid: src?.startsWith('data:image'),
    });
  }, [src]);

  // Handle mouse down for drag
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return; // Only left click
    
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setScrollStart({
      x: containerRef.current?.scrollLeft || 0,
      y: containerRef.current?.scrollTop || 0,
    });
  }, []);

  // Handle mouse move for drag
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;

    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;

    if (containerRef.current) {
      containerRef.current.scrollLeft = scrollStart.x - dx;
      containerRef.current.scrollTop = scrollStart.y - dy;
    }
  }, [isDragging, dragStart, scrollStart]);

  // Handle mouse up to stop drag
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Listen for mouseup globally in case mouse leaves container
  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);
    
    if (isDragging) {
      window.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging]);

  return (
    <div
      ref={containerRef}
      className={`overflow-auto bg-gray-900 ${className}`}
      style={{
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div
        className="inline-block min-w-full min-h-full"
        style={{
          transform: `scale(${zoom})`,
          transformOrigin: 'top left',
        }}
      >
        <img
          src={src}
          alt={alt}
          className="block max-w-none"
          draggable={false}
          style={{ userSelect: 'none' }}
        />
      </div>
    </div>
  );
};
